import ReactQuill from 'react-quill';

const Delta = ReactQuill.Quill.import('delta');

const MAX_TEXT_LENGTH = 40000;

const SummaryScope = {
  SELECTION: 'selection',
  NOTES: 'notes',
};

const getSummaryScope = (hasSelection) => (
  hasSelection ? SummaryScope.SELECTION : SummaryScope.NOTES
);

const getSummarizableText = (quill, range) => quill.getContents(range?.index, range?.length).ops
  .filter((op) => typeof op.insert === 'string' && !op.attributes?.strike)
  .map((op) => op.insert)
  .join('')
  .trim();

const buildSummaryDelta = (bullets) => bullets.reduce(
  (delta, bullet) => delta.insert(bullet).insert('\n', { list: 'bullet' }),
  new Delta(),
);

// Replaces `range` — or the whole document, when it is null — with `bullets` as a bullet list.
const replaceWithSummary = ({ quill, range, bullets }) => {
  // Guards against wiping the notes if this is ever reached without a summary to write.
  if (!bullets?.length) return;
  const summary = buildSummaryDelta(bullets);
  if (!range) {
    quill.setContents(summary, 'user');
    return;
  }

  const end = range.index + range.length;
  // Bullets have to start their own line, so break out of the one the selection began in.
  const startsMidLine = range.index > 0 && quill.getText(range.index - 1, 1) !== '\n';
  // The selection's trailing newline is now redundant (the last bullet supplies one), but
  // Quill's document always ends with a newline at `length - 1` that has to survive the edit.
  const absorbsLineBreak = end < quill.getLength() - 1 && quill.getText(end, 1) === '\n';

  const change = new Delta().retain(range.index);
  if (startsMidLine) {
    change.insert('\n', quill.getLine(range.index)?.[0]?.formats?.() || {});
  }

  quill.updateContents(
    change.concat(summary).delete(range.length + (absorbsLineBreak ? 1 : 0)),
    'user',
  );
};

const summarizeNotes = async ({ text, remote }) => {
  const response = await remote('clientNotes-summarize', { text });
  if (!response.ok) {
    throw new Error(`Summarize request failed with status ${response.status}`);
  }
  const { data } = await response.json();
  return Array.isArray(data) ? Array.from(new Set(data)) : [];
};

export {
  summarizeNotes,
  getSummarizableText,
  getSummaryScope,
  replaceWithSummary,
  SummaryScope,
  MAX_TEXT_LENGTH,
};
